import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, Truck, Package, Users, Building2, MapPin,
  Briefcase, UserCog, FileBarChart2, ScrollText, LogOut, Search, Menu, X, ExternalLink
} from "lucide-react";

const MENU = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin","company_admin","manager","operator"] },
  { to: "/app/consignments", label: "Consignments", icon: Package, roles: ["super_admin","company_admin","manager","operator"] },
  { to: "/app/shippers", label: "Shippers", icon: Briefcase, roles: ["super_admin","company_admin","manager","operator"] },
  { to: "/app/consignees", label: "Consignees", icon: Briefcase, roles: ["super_admin","company_admin","manager","operator"] },
  { to: "/app/branches", label: "Branches", icon: MapPin, roles: ["super_admin","company_admin","manager"] },
  { to: "/app/vehicles", label: "Vehicles", icon: Truck, roles: ["super_admin","company_admin","manager","operator"] },
  { to: "/app/agents", label: "Agents", icon: Users, roles: ["super_admin","company_admin","manager"] },
  { to: "/app/users", label: "Users", icon: UserCog, roles: ["super_admin","company_admin"] },
  { to: "/app/companies", label: "Companies", icon: Building2, roles: ["super_admin"] },
  { to: "/app/reports", label: "Reports", icon: FileBarChart2, roles: ["super_admin","company_admin","manager"] },
  { to: "/app/activity", label: "Activity Logs", icon: ScrollText, roles: ["super_admin","company_admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const visible = MENU.filter(m => m.roles.includes(user?.role));
  const [trackLR, setTrackLR] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);
  // Lock body scroll while drawer open on mobile
  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const SidebarInner = (
    <>
      <div className="lt-brand px-5 py-4 flex items-center gap-2 flex-shrink-0">
        <div className="bg-sky-400 w-9 h-9 rounded-md grid place-items-center font-bold text-slate-900">RCC</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold tracking-wide text-white truncate">Ritik Cargo</div>
          <div className="text-[11px] text-slate-300 -mt-0.5">Carriers · TMS</div>
        </div>
        <button
          type="button"
          className="md:hidden text-slate-300 hover:text-white p-1"
          onClick={() => setDrawerOpen(false)}
          aria-label="Close menu"
          data-testid="drawer-close"
        >
          <X size={20} />
        </button>
      </div>
      <nav className="flex-1 py-3 scrollbar-thin overflow-y-auto">
        {visible.map(m => (
          <NavLink
            key={m.to}
            to={m.to}
            end={m.to === "/app"}
            data-testid={`nav-${m.label.toLowerCase().replace(/\s+/g,"-")}`}
            className={({isActive}) => `flex items-center gap-3 px-5 py-2.5 text-sm transition ${isActive ? "active" : ""}`}
          >
            <m.icon size={18} /> {m.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-5 py-4 border-t border-slate-800 text-xs text-slate-400">
        <div className="font-semibold text-slate-200" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="capitalize">{user?.role?.replace("_"," ")}</div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Desktop static sidebar (>=md) */}
      <aside className="lt-sidebar w-64 flex-shrink-0 hidden md:flex flex-col" data-testid="sidebar-desktop">
        {SidebarInner}
      </aside>

      {/* Mobile drawer + backdrop (<md) */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-backdrop"
        />
      )}
      <aside
        className={`lt-sidebar w-64 fixed inset-y-0 left-0 z-50 flex flex-col md:hidden transform transition-transform duration-200 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar-mobile"
        aria-hidden={!drawerOpen}
      >
        {SidebarInner}
      </aside>

      {/* Content column */}
      <div className="flex-1 flex flex-col min-w-0 w-full">
        <header className="lt-topbar flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-3">
          {/* Hamburger (mobile only) */}
          <button
            type="button"
            className="md:hidden p-2 -ml-1 rounded-md text-slate-700 hover:bg-slate-100"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            data-testid="hamburger-btn"
          >
            <Menu size={22} />
          </button>

          {/* Search: takes remaining space */}
          <form
            onSubmit={(e) => { e.preventDefault(); if (trackLR) nav(`/app/consignments?q=${trackLR}`); }}
            className="flex items-center flex-1 min-w-0"
          >
            <div className="relative w-full max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="topbar-search"
                value={trackLR}
                onChange={e => setTrackLR(e.target.value)}
                placeholder="Search LR..."
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-sky-500"
              />
            </div>
          </form>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            <a
              href="/track"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline text-sm text-sky-700 hover:underline"
              data-testid="public-track-link"
            >
              Public Track
            </a>
            <a
              href="/track"
              target="_blank"
              rel="noreferrer"
              className="sm:hidden p-2 text-sky-700 hover:bg-slate-100 rounded-md"
              aria-label="Public Track"
              data-testid="public-track-icon"
            >
              <ExternalLink size={18} />
            </a>
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-btn"
              className="flex items-center gap-1.5 text-sm px-2.5 sm:px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700"
              aria-label="Logout"
            >
              <LogOut size={15} /><span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>
        <main className="p-4 sm:p-6 flex-1 overflow-y-auto overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
